import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Dialog, Input } from "./Suppliers";
import { toast } from "sonner";
import useSubmitGuard from "../lib/useSubmitGuard";
import { Edit3, Trash2, GitMerge, Power, Plus, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { fmtIST } from "../lib/time";

/**
 * Wave F (Feb-2026) — Editable Material Categories (Super Admin).
 *
 * CRUD + merge + activate/deactivate for material categories. Every write
 * is audit-logged with previous → new values, user name, timestamp and a
 * mandatory operator-supplied reason.
 *
 * Non-SA users can still SEE the list (read-only) so they can pick a
 * category from Materials — but every edit button is hidden for them.
 */
export default function MaterialCategories() {
  const { user } = useAuth();
  const isSA = (user?.role_name || "") === "Super Admin";
  const { busy, guard, reset } = useSubmitGuard();

  const [rows, setRows] = useState([]);
  const [audit, setAudit] = useState([]);
  const [dlg, setDlg] = useState(null);   // { mode:"new"|"edit"|"del"|"merge"|"toggle", cat, ... }
  const [form, setForm] = useState({});

  const load = async () => {
    const [c, a] = await Promise.all([
      api.get("/material-categories"),
      isSA ? api.get("/audit-log", { params: { module: "material_categories" } }).catch(() => ({ data: [] }))
           : Promise.resolve({ data: [] }),
    ]);
    setRows(c.data || []);
    setAudit((a.data || []).slice(0, 25));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setForm({ name: "", prefix: "" }); setDlg({ mode: "new" }); };
  const openEdit = (cat) => { setForm({ name: cat.name, prefix: cat.prefix || "", reason: "" }); setDlg({ mode: "edit", cat }); };
  const openDel = (cat) => { setForm({ reason: "" }); setDlg({ mode: "del", cat }); };
  const openMerge = (cat) => { setForm({ target_id: "", reason: "" }); setDlg({ mode: "merge", cat }); };
  const openToggle = (cat) => { setForm({ reason: "" }); setDlg({ mode: "toggle", cat, target: !(cat.active !== false) }); };
  const close = () => { setDlg(null); reset(); };

  const submit = async () => {
    try {
      await guard(async () => {
        if (dlg.mode === "new") {
          await api.post("/material-categories", { name: form.name, prefix: form.prefix });
          toast.success("Category created");
        } else if (dlg.mode === "edit") {
          await api.put(`/material-categories/${dlg.cat.id}`, {
            name: form.name, prefix: form.prefix, reason: form.reason,
          });
          toast.success("Category updated");
        } else if (dlg.mode === "del") {
          await api.delete(`/material-categories/${dlg.cat.id}`, { params: { reason: form.reason } });
          toast.success("Category deleted");
        } else if (dlg.mode === "merge") {
          const r = await api.post("/material-categories/merge", {
            source_id: dlg.cat.id, target_id: form.target_id, reason: form.reason,
          });
          toast.success(`Merged — ${r.data.materials_moved} material(s) moved`);
        } else if (dlg.mode === "toggle") {
          await api.put(`/material-categories/${dlg.cat.id}`, { active: dlg.target, reason: form.reason });
          toast.success(dlg.target ? "Category activated" : "Category deactivated");
        }
        close(); load();
      });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="material-categories-page">
      <PageHeader title="Material Categories" subtitle="Central taxonomy · Super Admin controlled with full audit"
        right={isSA ? (
          <button onClick={openNew} data-testid="new-cat-btn"
            className="bg-[#1565C0] hover:bg-[#0D2B5E] text-white text-sm px-4 py-2 rounded-sm inline-flex items-center gap-2 uppercase tracking-wider font-semibold">
            <Plus size={14}/> New Category
          </button>
        ) : null}/>
      <div className="p-8 grid grid-cols-3 gap-6">
        <div className="col-span-2 bg-white border border-[#EEEEEE] rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Prefix</th>
                <th className="text-center px-3 py-2">Active</th>
                <th className="text-left px-3 py-2">Created</th>
                {isSA && <th className="text-right px-3 py-2">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((c, i) => (
                <tr key={c.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                  <td className="px-3 py-2 font-semibold text-[#0D2B5E]">{c.name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-[#1565C0]">{c.prefix || <span className="text-[#0D2B5E]/30">—</span>}</td>
                  <td className="px-3 py-2 text-center">
                    {c.active !== false
                      ? <span className="text-[10px] font-bold text-[#2E7D32] uppercase tracking-widest">Active</span>
                      : <span className="text-[10px] font-bold text-[#0D2B5E]/40 uppercase tracking-widest">Inactive</span>}
                  </td>
                  <td className="px-3 py-2 text-xs text-[#0D2B5E]/70">{c.created_by || "—"} · {(c.created_at || "").slice(0, 10)}</td>
                  {isSA && (
                    <td className="px-3 py-2 text-right space-x-1">
                      <IconBtn onClick={() => openEdit(c)} title="Rename / re-prefix" testId={`edit-cat-${c.id}`}><Edit3 size={14}/></IconBtn>
                      <IconBtn onClick={() => openToggle(c)} title={c.active !== false ? "Deactivate" : "Activate"}><Power size={14}/></IconBtn>
                      <IconBtn onClick={() => openMerge(c)} title="Merge into another category"><GitMerge size={14}/></IconBtn>
                      <IconBtn onClick={() => openDel(c)} title="Delete (only if unused)" tone="red" testId={`del-cat-${c.id}`}><Trash2 size={14}/></IconBtn>
                    </td>
                  )}
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-sm text-[#0D2B5E]/50">No categories yet. Click <b>New Category</b> to add one.</td></tr>}
            </tbody>
          </table>
        </div>
        {isSA && (
          <div className="col-span-1 bg-white border border-[#EEEEEE] rounded-sm">
            <div className="px-3 py-2 border-b border-[#EEEEEE] bg-[#F5F5F5] flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#0D2B5E]/70">
              <ShieldCheck size={12}/> Audit trail
            </div>
            <div className="max-h-[70vh] overflow-y-auto">
              {audit.length === 0 && <div className="p-4 text-xs text-[#0D2B5E]/50">No changes logged yet.</div>}
              {audit.map((a) => (
                <div key={a.id} className="border-b border-[#EEEEEE] px-3 py-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className={`text-[9px] tracking-widest uppercase font-bold ${
                      a.action === "delete" ? "text-[#C62828]" : a.action === "create" ? "text-[#2E7D32]" : "text-[#1565C0]"
                    }`}>{a.action}</span>
                    <span className="text-[10px] text-[#0D2B5E]/50 font-mono">{fmtIST(a.timestamp)}</span>
                  </div>
                  <div className="text-[#0D2B5E]/80">{a.user_name} <span className="text-[#0D2B5E]/40">·</span> {a.role_name}</div>
                  {a.reason && <div className="mt-1 text-[10px] italic text-[#0D2B5E]/70">&ldquo;{a.reason}&rdquo;</div>}
                  {(a.previous_state || a.new_state) && (
                    <div className="text-[10px] font-mono text-[#0D2B5E]/60 mt-1">
                      {a.previous_state && <div>prev: {JSON.stringify(a.previous_state)}</div>}
                      {a.new_state && <div>new: {JSON.stringify(a.new_state)}</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {dlg && (
        <Dialog title={
          dlg.mode === "new" ? "New Category" :
          dlg.mode === "edit" ? `Edit ${dlg.cat.name}` :
          dlg.mode === "del" ? `Delete ${dlg.cat.name}` :
          dlg.mode === "merge" ? `Merge ${dlg.cat.name} → …` :
          `${dlg.target ? "Activate" : "Deactivate"} ${dlg.cat.name}`
        } onClose={close}>
          {(dlg.mode === "new" || dlg.mode === "edit") && (
            <>
              <Input label="Name *" value={form.name || ""} onChange={(v) => setForm({ ...form, name: v })} testId="cat-name"/>
              <Input label="Prefix (A-Z, 0-9; 2-5 chars — used for material code auto-gen)"
                value={form.prefix || ""} onChange={(v) => setForm({ ...form, prefix: v.toUpperCase() })} testId="cat-prefix"/>
              {dlg.mode === "edit" && <Input label="Reason for change *" value={form.reason || ""} onChange={(v) => setForm({ ...form, reason: v })} testId="cat-reason"/>}
            </>
          )}
          {dlg.mode === "del" && (
            <>
              <div className="text-sm mb-3 text-[#C62828]">
                Delete <b>{dlg.cat.name}</b> permanently? Only unused categories can be deleted.
              </div>
              <Input label="Reason (mandatory, ≥ 3 chars)" value={form.reason || ""} onChange={(v) => setForm({ ...form, reason: v })} testId="cat-reason"/>
            </>
          )}
          {dlg.mode === "toggle" && (
            <>
              <div className="text-sm mb-3 text-[#0D2B5E]/80">
                {dlg.target ? "Re-enable" : "Deactivate"} <b>{dlg.cat.name}</b>? Deactivated categories stay in the DB but are hidden in the material picker for new records.
              </div>
              <Input label="Reason" value={form.reason || ""} onChange={(v) => setForm({ ...form, reason: v })}/>
            </>
          )}
          {dlg.mode === "merge" && (
            <>
              <div className="text-sm mb-3">
                Move every material from <b>{dlg.cat.name}</b> into another category, then delete <b>{dlg.cat.name}</b>.
              </div>
              <label className="block text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold mb-1">Merge into *</label>
              <select value={form.target_id || ""} onChange={(e) => setForm({ ...form, target_id: e.target.value })}
                data-testid="cat-merge-target" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm mb-3">
                <option value="">Select target…</option>
                {rows.filter((r) => r.id !== dlg.cat.id).map((r) => <option key={r.id} value={r.id}>{r.name}{r.prefix ? ` [${r.prefix}]` : ""}</option>)}
              </select>
              <Input label="Reason" value={form.reason || ""} onChange={(v) => setForm({ ...form, reason: v })}/>
            </>
          )}
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={close} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={submit} disabled={busy} data-testid="cat-submit-btn"
              className={`px-4 py-2 text-white text-sm rounded-sm font-semibold tracking-wider uppercase disabled:opacity-50 disabled:cursor-not-allowed ${
                dlg.mode === "del" ? "bg-[#C62828]" : "bg-[#1565C0]"
              }`}>
              {busy ? "Working…" : dlg.mode === "del" ? "Delete" : dlg.mode === "toggle" ? (dlg.target ? "Activate" : "Deactivate") : dlg.mode === "merge" ? "Merge & Delete" : "Save"}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function IconBtn({ onClick, title, testId, tone = "blue", children }) {
  const t = tone === "red" ? "text-[#C62828] hover:bg-[#C62828]/10" : "text-[#0D2B5E] hover:bg-[#0D2B5E]/10";
  return <button onClick={onClick} title={title} data-testid={testId}
    className={`inline-flex items-center p-1.5 rounded-sm border border-transparent hover:border-current transition ${t}`}>
    {children}
  </button>;
}
